import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { getDate } from '../../components/utils/helper';
import { getTransaction, transactionView } from '../../components/actions/campaign';
import Loader from '../../components/utils/Loader';
import * as XLSX from 'xlsx';
import { Link } from 'react-router-dom';

const TransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [qualifiedFilter, setQualifiedFilter] = useState('All');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const code = sessionStorage.getItem('code');

  // Fetch transactions on mount
  useEffect(() => {
    setLoading(true);
    getTransaction(code)
      .then((res) => {
        const transactionData = res?.data || [];
        setTransactions(transactionData);
        setFilteredTransactions(transactionData);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching transactions:', error);
        setLoading(false);
      });
  }, []);

  // Filter transactions based on search term and filters
  useEffect(() => {
    const lowerCaseFilter = searchTerm.toLowerCase();
    const filtered = transactions.filter((transaction) => {
      const matchesSearch =
        (transaction.transactionId?.toLowerCase().includes(lowerCaseFilter) ||
         transaction.customerid?.toLowerCase().includes(lowerCaseFilter));

      const matchesStatus = statusFilter === 'All Status' || transaction.status === statusFilter;
      const matchesQualified =
        qualifiedFilter === 'All' ||
        (qualifiedFilter === 'Qualified' && transaction.isqualified) ||
        (qualifiedFilter === 'Not Qualified' && !transaction.isqualified);

      return matchesSearch && matchesStatus && matchesQualified;
    });
    setFilteredTransactions(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, qualifiedFilter, transactions]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredTransactions.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const paginatedTransactions = filteredTransactions.slice(startIndex, startIndex + entriesPerPage);
  const totalTransactions = transactions.length;
  const qualifiedTransactions = transactions.filter((t) => t.isqualified).length;
  const totalAmount = transactions.reduce(
  (sum, t) => sum + Number(t.amount || 0),
  0
);
  const totalPoints = transactions.reduce((sum, t) => sum + (t.points || 0), 0);

  // Copy to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess('Copied');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy text', err);
      setCopySuccess('Failed to copy');
    }
  };

  // View transaction details
  const viewTransaction = async (transactionId) => {
    setLoading(true);
    try {
      const res = await transactionView(code, transactionId);
      setSelectedTransaction(res?.data || {});
      setShowTransactionModal(true);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      setLoading(false);
    }
  };

  // Export transactions to Excel
  const exportToExcel = () => {
    // Prepare data for Excel
    const data = filteredTransactions.map((transaction) => ({
      'Transaction ID': transaction.transactionId || '-',
      'Customer ID': transaction.customerid || '-',
      'Date': formatDate(transaction.CreatedOn),
      'Is Qualified': transaction.isqualified ? 'Yes' : 'No',
      'Status': transaction.status || '-',
      'Amount': transaction.amount ? `${transaction.amount.toLocaleString('en-IN')}` : '-',
      'Points': transaction.points ? transaction.points.toLocaleString('en-IN') : '-',
    }));

    // Create a worksheet
    const worksheet = XLSX.utils.json_to_sheet(data);
    
    // Create a workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');

    // Customize headers (optional)
    worksheet['!cols'] = [
      { wch: 20 }, // Transaction ID
      { wch: 20 }, // Customer ID
      { wch: 15 }, // Date
      { wch: 15 }, // Is Qualified
      { wch: 15 }, // Status
      { wch: 15 }, // Amount
      { wch: 15 }, // Points
    ];

    // Generate Excel file and trigger download
    XLSX.writeFile(workbook, `Transactions_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getQualifiedColor = (isQualified) => {
    return isQualified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const formatDate = (dateString) => {
    return getDate(dateString, 'DD-MM-YYYY') || '-';
  };

  return (
    <DashboardLayout>
      {loading && <Loader />}
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
     <div className="flex items-center justify-between mb-8">
  <div>
    <h1 className="text-3xl font-bold text-gray-900">Transaction Management</h1>
    <p className="text-gray-600 mt-1">Track and manage all customer transactions</p>
  </div>

  {/* Buttons Wrapper */}
  <div className="flex items-center gap-4">
    <Link
      to="/post-transaction"
      className="bg-blue-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-600 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap"
    >
      <i className="ri-add-line text-lg"></i>
      Post Transaction
    </Link>

    <button
      onClick={exportToExcel}
      className="bg-blue-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-600 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap"
    >
      <i className="ri-download-line text-lg"></i>
      Export Transactions
    </button>
  </div>
</div>


        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <i className="ri-exchange-line text-blue-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Transactions</p>
                <p className="text-2xl font-bold text-gray-900">{totalTransactions}</p>
                <p className="text-green-600 text-xs font-semibold">↗ +12% this month</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Qualified Transactions</p>
                <p className="text-2xl font-bold text-gray-900">{qualifiedTransactions}</p>
                <p className="text-green-600 text-xs font-semibold">↗ +8.5% growth</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <i className="ri-money-rupee-circle-line text-purple-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Amount</p>
                <p className="text-2xl font-bold text-gray-900">{totalAmount}</p>
                <p className="text-green-600 text-xs font-semibold">↗ +15.2% increase</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <i className="ri-coin-line text-orange-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Points</p>
                <p className="text-2xl font-bold text-gray-900">{totalPoints.toLocaleString('en-IN')}</p>
                <p className="text-green-600 text-xs font-semibold">↗ +22% vs last month</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 items-center w-full lg:w-auto">
              <div className="relative w-full sm:w-80">
                <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search transactions by ID or customer ID..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-sm bg-gray-50"
                />
              </div>
            </div>

            <div className="flex gap-3 items-center">
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="appearance-none bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                >
                  <option value="All Status">All Status</option>
                  <option value="Completed">Completed</option>
                  <option value="Pending">Pending</option>
                  <option value="Failed">Failed</option>
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
              </div>

              <div className="relative">
                <select
                  value={qualifiedFilter}
                  onChange={(e) => setQualifiedFilter(e.target.value)}
                  className="appearance-none bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                >
                  <option value="All">All</option>
                  <option value="Qualified">Qualified</option>
                  <option value="Not Qualified">Not Qualified</option>
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show</span>
                <select
                  value={entriesPerPage}
                  onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                  className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer pr-8"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>
            </div>

            <div className="text-sm text-gray-600">
              Showing {filteredTransactions.length} results
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaction ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Is Qualified
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : paginatedTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                      No data found
                    </td>
                  </tr>
                ) : (
                  paginatedTransactions.map((transaction) => (
                    <tr key={transaction.transactionId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.transactionId || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.customerid || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(transaction.CreatedOn)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getQualifiedColor(
                            transaction.isqualified
                          )}`}
                        >
                          {transaction.isqualified ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => viewTransaction(transaction.transactionId)}
                          className="text-blue-600 hover:text-blue-800 cursor-pointer bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg transition-colors"
                          title="View Transaction Details"
                        >
                          <i className="ri-eye-line text-lg"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mt-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {filteredTransactions.length === 0 ? 0 : startIndex + 1} to{' '}
              {Math.min(startIndex + entriesPerPage, filteredTransactions.length)} of{' '}
              {filteredTransactions.length} entries
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                Previous
              </button>

              {[...Array(Math.min(5, totalPages))].map((_, index) => {
                const pageNum = index + 1;
                if (pageNum > totalPages) return null;

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 border rounded text-sm cursor-pointer ${
                      currentPage === pageNum
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Transaction Detail Modal */}
        {showTransactionModal && selectedTransaction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Transaction Details</h2>
                <button
                  onClick={() => setShowTransactionModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Transaction ID</p>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-gray-900 font-mono">
                        {selectedTransaction?.transactions?.[0]?.transactionId || '-'}
                      </p>
                      <button
                        onClick={() => copyToClipboard(selectedTransaction?.transactions?.[0]?.transactionId)}
                        className="text-blue-600 hover:text-blue-800 cursor-pointer"
                        title="Copy Transaction ID"
                      >
                        <i className="ri-file-copy-line text-sm"></i>
                      </button>
                      {copySuccess && (
                        <span className="text-green-600 text-xs font-medium">{copySuccess}</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Customer ID</p>
                    <p className="text-sm text-gray-900 font-mono">
                      {selectedTransaction?.transactions?.[0]?.customerid || '-'}
                    </p>
                  </div>
                </div>

                <hr className="border-gray-200" />

                {/* Transaction Details */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Transaction Details</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600 mb-1">Date</p>
                        <p className="text-sm text-gray-900">
                          {formatDate(selectedTransaction?.transactions?.[0]?.CreatedOn)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600 mb-1">Is Qualified</p>
                        <p className="text-sm text-gray-900">
                          {selectedTransaction?.transactions?.[0]?.isqualified ? 'Yes' : 'No'}
                        </p>
                      </div>
                      {Object.entries(selectedTransaction?.transactions?.[0] || {})
                        .filter(
                          ([key, value]) =>
                            !['transactionId', 'customerid', 'CreatedOn', 'isqualified', 'clientid'].includes(key) &&
                            value !== null
                        )
                        .map(([key, value]) => (
                          <div key={key}>
                            <p className="text-sm font-medium text-gray-600 mb-1">
                              {key?.charAt(0).toUpperCase() + key.slice(1)}
                            </p>
                            <p className="text-sm text-gray-900">{value.toString() || '-'}</p>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>

                {/* Qualified Details */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Qualified Details</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                    {selectedTransaction?.transactionDetails?.length > 0 ? (
                      selectedTransaction.transactionDetails.map((detail, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-lg p-4 bg-white"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-gray-600 mb-1">Campaign ID</p>
                              <p className="text-sm text-gray-900">{detail.campaignid || '-'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600 mb-1">Earning Date</p>
                              <p className="text-sm text-gray-900">{formatDate(detail.earningdate)}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600 mb-1">Earning Type</p>
                              <p className="text-sm text-gray-900">{detail.earningtype || '-'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600 mb-1">Earning Value</p>
                              <p className="text-sm text-gray-900">{detail.earningvalue || '-'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600 mb-1">Earning Value Type</p>
                              <p className="text-sm text-gray-900">{detail.earningvaluetype || '-'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600 mb-1">Redeem After Date</p>
                              <p className="text-sm text-gray-900">{formatDate(detail.redeemafterDate)}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600 mb-1">Expiration Date</p>
                              <p className="text-sm text-gray-900">{formatDate(detail.expirationDate)}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600 mb-1">Transaction Type</p>
                              <p className="text-sm text-gray-900">{detail.tranType || '-'}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-900">No qualified details available</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => setShowTransactionModal(false)}
                    className="bg-blue-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-600 transition-colors cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TransactionsPage;